/**
 * usePushNotifications
 *
 * React hook that handles native push notifications inside the Capacitor shell.
 * - No-ops on plain web (only runs when Capacitor.isNativePlatform() is true)
 * - Requests permission, obtains an FCM token, registers it with the backend
 * - Listens for foreground messages and surfaces them as sonner toasts
 * - Handles tap-to-open by routing to the related entity via the injected navigate fn
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

let _cachedCapacitor = null;
let _cachedMessaging = null;

async function loadCapacitor() {
  if (_cachedCapacitor) return _cachedCapacitor;
  try {
    const mod = await import('@capacitor/core');
    _cachedCapacitor = mod.Capacitor;
    return _cachedCapacitor;
  } catch (e) {
    console.warn('[push] Capacitor not available:', e?.message);
    return null;
  }
}

async function loadMessaging() {
  if (_cachedMessaging) return _cachedMessaging;
  try {
    const mod = await import('@capacitor-firebase/messaging');
    _cachedMessaging = mod.FirebaseMessaging;
    return _cachedMessaging;
  } catch (e) {
    console.warn('[push] FirebaseMessaging plugin not available:', e?.message);
    return null;
  }
}

export function usePushNotifications({ enabled = true, onOpen } = {}) {
  const [supported, setSupported] = useState(false);
  const [permissionState, setPermissionState] = useState('unknown'); // granted | denied | prompt | unknown
  const [fcmToken, setFcmToken] = useState(null);
  const [registering, setRegistering] = useState(false);

  // Detect environment on mount
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const Capacitor = await loadCapacitor();
      if (cancelled) return;
      const isNative = !!Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
      setSupported(isNative);
      if (isNative) {
        const FirebaseMessaging = await loadMessaging();
        if (FirebaseMessaging) {
          try {
            const { receive } = await FirebaseMessaging.checkPermissions();
            setPermissionState(receive);
          } catch (e) {
            console.warn('[push] checkPermissions failed:', e?.message);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Foreground message + tap-open listeners
  useEffect(() => {
    if (!enabled || !supported) return;
    let cleanup = () => {};
    (async () => {
      const FirebaseMessaging = await loadMessaging();
      if (!FirebaseMessaging) return;

      const foregroundHandle = await FirebaseMessaging.addListener(
        'notificationReceived',
        (event) => {
          const n = event?.notification || {};
          const title = n.title || 'SafetyMind AI';
          const body = n.body || '';
          toast(title, { description: body });
        },
      );

      const tapHandle = await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        (event) => {
          const data = event?.notification?.data || {};
          if (typeof onOpen === 'function') {
            try {
              onOpen(data);
            } catch (e) {
              console.warn('[push] onOpen handler threw:', e);
            }
          }
        },
      );

      cleanup = () => {
        try { foregroundHandle?.remove?.(); } catch (_) { /* noop */ }
        try { tapHandle?.remove?.(); } catch (_) { /* noop */ }
      };
    })();
    return () => cleanup();
  }, [enabled, supported, onOpen]);

  const registerForPush = useCallback(async () => {
    setRegistering(true);
    try {
      const Capacitor = await loadCapacitor();
      const FirebaseMessaging = await loadMessaging();
      if (!Capacitor || !Capacitor.isNativePlatform() || !FirebaseMessaging) {
        toast.error('Push notifications are only available inside the mobile app.');
        return { ok: false, reason: 'not_native' };
      }

      const { receive } = await FirebaseMessaging.requestPermissions();
      setPermissionState(receive);
      if (receive !== 'granted') {
        toast.error('Notification permission was declined. You can re-enable it in device settings.');
        return { ok: false, reason: 'permission_denied' };
      }

      const { token } = await FirebaseMessaging.getToken();
      if (!token) {
        toast.error('Could not obtain a push token from Firebase.');
        return { ok: false, reason: 'no_token' };
      }
      setFcmToken(token);

      const platform = Capacitor.getPlatform();
      await axiosInstance.post('/push/register', {
        token,
        platform,
      });

      toast.success('Push notifications enabled on this device.');
      return { ok: true, token, platform };
    } catch (e) {
      console.error('[push] registration failed:', e);
      toast.error('Failed to enable push notifications.');
      return { ok: false, reason: 'error', error: e?.message };
    } finally {
      setRegistering(false);
    }
  }, []);

  const unregisterForPush = useCallback(async () => {
    setRegistering(true);
    try {
      const FirebaseMessaging = await loadMessaging();
      const currentToken = fcmToken || (FirebaseMessaging ? (await FirebaseMessaging.getToken()).token : null);
      if (currentToken) {
        await axiosInstance.post('/push/unregister', { token: currentToken });
      }
      if (FirebaseMessaging) {
        try { await FirebaseMessaging.deleteToken(); } catch (_) { /* noop */ }
      }
      setFcmToken(null);
      toast.success('Push notifications disabled on this device.');
      return { ok: true };
    } catch (e) {
      console.error('[push] unregister failed:', e);
      toast.error('Failed to disable push notifications.');
      return { ok: false, error: e?.message };
    } finally {
      setRegistering(false);
    }
  }, [fcmToken]);

  return {
    supported,             // true only inside the native Capacitor shell
    permissionState,       // granted | denied | prompt | unknown
    fcmToken,
    registering,
    registerForPush,
    unregisterForPush,
  };
}

export default usePushNotifications;
